<script setup lang="ts">
import { shallowRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { ChevronDown, ShoppingCart, Ticket, TicketPlus } from 'lucide-vue-next'
import { useGeneratorClientStore } from '@/stores/GeneratorClient'
import { useAuthStore } from '@/stores/authStore'
import Tools from '@/utils/tools'
import { useClickOutside } from '@/composables/useClickOutside'
import { SubscriptionType } from '@/types'
import EnergyIcon from '@/icons/EnergyIcon.vue'

const { t } = useI18n()
const show = shallowRef<boolean>(false)
const authStore = useAuthStore()
const { user } = storeToRefs(authStore)

const panel_ref = shallowRef<HTMLElement | null>(null)

useClickOutside(show, panel_ref, {
	excludeSelectors: ['.ticket-tracker'],
	closeOnScroll: true,
})
</script>

<template>
	<div class="relative flex gap-1 flex-nowrap">
		<span
			v-if="!user.daily_quest_completed && !show"
			class="absolute -top-2 -right-2 chip-daily-task z-[1]"
		>1</span>
		<Button
			:class="{
				'button-energy': authStore.hasDailyTickets,
				'button-ticket': !authStore.hasDailyTickets,
			}"
			class="ticket-tracker"
			@click="show = !show"
		>
			<template #default>
				<component
					:is="authStore.hasDailyTickets ? EnergyIcon : Ticket"
					v-bind="{ variant: 'normal' }"
					class="lucide-button-icon"
				/>
				<span v-if="!authStore.hasDailyTickets">
					{{ user.generation_token > 10000 ? Tools.formatLargeNumber(user.generation_token) : Tools.formatNumber(user.generation_token) }}
				</span>
				<span v-else>
					{{ user.daily_token }}
				</span>
				<ChevronDown class="lucide-button-icon" />
			</template>
		</Button>
		<Transition name="list">
			<Teleport to="body">
				<div
					v-show="show"
					ref="panel_ref"
					class="flex flex-nowrap max-w-screen gap-2 p-2 fixed top-16 bg-slate-950 right-1 sm:right-8 border-[1px] border-solid border-slate-700 rounded-lg z-[100000]"
				>
					<div class="flex flex-col rounded-lg gap-6 justify-center p-2">
						<div class="flex flex-col items-center gap-2 whitespace-nowrap">
							<div class="button-rainbow-shade !rounded-xl !w-8 !h-8 !p-0">
								<Ticket
									class="lucide-button-icon-sm"
								/>
							</div>
							<div class="flex items-center gap-1">
								<span class="font-semibold">
									{{ user.generation_token > 10000 ? Tools.formatLargeNumber(user.generation_token) : Tools.formatNumber(user.generation_token) }}
								</span>
								<span class="text-slate-300 text-xsp">
									Tickets
								</span>
							</div>
						</div>
						<div class="flex items-center flex-col gap-2">
							<EnergyIcon
								variant="electric"
								class="lucide-button-icon-lg"
							/>

							<div
								v-if="authStore.isPremium"
								class="flex items-center gap-1"
							>
								<span class="font-semibold">
									{{ user.daily_token }}
								</span>
								<span class="text-slate-300 text-xsp">
									{{ t('common.energy') }}
								</span>
							</div>
							<router-link
								v-else
								to="/tickets"
								class="hyperlink-discret tracking-tight font-semibold !text-sm whitespace-nowrap"
								@click="show = false"
							>
								{{ user.plan > SubscriptionType.Free ? t('common.pro_plan') : t('common.free_plan') }}
							</router-link>
						</div>
					</div>

					<div class="flex flex-col gap-4">
						<router-link
							to="/daily-tasks"
							class="max-w-[180px] sm:max-w-[300px] no-underline h-[50%] text-white cursor-pointer duration-300 hover:bg-slate-800 flex flex-nowrap gap-2 items-center bg-slate-900 p-2 rounded-lg border-[1px] border-solid border-slate-700"
							@click="show = false"
						>
							<div>
								<TicketPlus class="lucide-button-icon" />
							</div>

							<div class="flex flex-col items-start">
								<span class="text-slate-200 font-semibold text-sm flex justify-between items-center w-full">
									{{ t('daily_tasks.title') }}
									<span
										v-if="!user.daily_quest_completed"
										class="chip-daily-task"
									>1</span>
								</span>
								<span class="text-sm tracking-tight text-slate-300">
									<template v-if="user.daily_quest_completed">
										{{ t('common.reset_midnight') }}
									</template>
									<template v-else>
										{{ t('daily_tasks.tracker_desc') }}
									</template>
								</span>
							</div>
						</router-link>
						<router-link
							to="/tickets"
							class="max-w-[180px] sm:max-w-[300px] no-underline h-[50%] text-white cursor-pointer duration-300 hover:bg-slate-800 flex flex-nowrap gap-2 items-center bg-slate-900 p-2 rounded-lg border-[1px] border-solid border-slate-700"
							@click="show = false"
						>
							<div>
								<ShoppingCart class="lucide-button-icon" />
							</div>

							<div class="flex flex-col items-start">
								<span class="text-slate-200 font-semibold text-sm">
									{{ t('common.buy_tickets') }}
								</span>
								<span class="text-sm tracking-tight text-slate-300">{{ t('common.get_more_tickets') }}</span>
							</div>
						</router-link>
					</div>
				</div>
			</Teleport>
		</Transition>
	</div>
</template>
